import React from "react";
import { Helmet } from "react-helmet-async";
import PageHeader from "../components/PageHeader";
import ContactForm from "../components/ContactForm";
import { Reveal, Section } from "../components/ui";
import { site, socialLinks } from "../data/site";

const faqs = [
  {
    q: "What does a typical engagement look like?",
    a: "Most projects run four to twelve weeks, split into a definition phase and build slices. You see working software early and often, not a reveal at the end.",
  },
  {
    q: "Do you take on design-only or build-only work?",
    a: "Yes. Plenty of work is one or the other — an API build against an existing frontend, a data pipeline, a CI/CD and deployment overhaul. The method is the same.",
  },
  {
    q: "How do you handle handover?",
    a: "Documentation, a component inventory and a walkthrough. The test is whether your team can extend the work without calling me, and that is deliberate.",
  },
  {
    q: "What if the budget is small?",
    a: "Say so plainly. Scope is adjustable and a well-defined small project is far better work than a vague large one. I will tell you honestly if I am the wrong fit.",
  },
];

const Contact = () => (
  <>
    <Helmet>
      <title>Contact — Jephthah Boateng</title>
      <meta
        name="description"
        content={`Start a project with ${site.name}. Backend and API engineering, data pipelines, frontend architecture and platform work.`}
      />
    </Helmet>

    <PageHeader
      eyebrow="Contact"
      title="Let's make something worth keeping."
      lead="Tell me what is broken and what would count as success. If I am the wrong person for it, I will say so and point you somewhere better."
      meta={[site.availability, "Replies within 2 working days", "GMT"]}
    />

    <Section tone="default">
      <div className="grid gap-14 lg:grid-cols-12">
        {/* Direct channels */}
        <div className="lg:col-span-4">
          <Reveal>
            <p className="t-eyebrow">Direct</p>
            <div className="mt-7 space-y-0">
              {socialLinks.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  target={s.url.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  className="group flex items-baseline justify-between gap-4 border-t border-[var(--color-border)] py-5 transition-colors duration-300 hover:border-azure dark:hover:border-electric"
                >
                  <span className="font-display text-2xl transition-colors duration-300 group-hover:text-azure dark:group-hover:text-electric">
                    {s.name}
                  </span>
                  <span className="text-right font-mono text-[10px] uppercase tracking-[0.14em] text-ink-muted dark:text-slightwhite/55">
                    {s.handle}
                  </span>
                </a>
              ))}
              <div className="rule" />
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-12 rounded-lg bg-gradient-brand p-8 text-paper">
              <p className="t-eyebrow text-electric-soft">Currently</p>
              <p className="mt-4 font-display text-3xl leading-tight">
                {site.availability}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-paper/75">
                Working remotely with distributed teams across Africa, Europe,
                North and South America, Asia and the Middle East.
              </p>
            </div>
          </Reveal>
        </div>

        {/* Form */}
        <div className="lg:col-span-8">
          <Reveal delay={0.1}>
            <ContactForm />
          </Reveal>
        </div>
      </div>
    </Section>

    {/* FAQ */}
    <Section tone="warm">
      <p className="t-eyebrow">Before you write</p>
      <h2 className="t-title mt-6 max-w-2xl">Questions I am usually asked.</h2>

      <div className="mt-14 grid gap-x-12 gap-y-10 md:grid-cols-2">
        {faqs.map((f, i) => (
          <Reveal key={f.q} delay={i * 0.06}>
            <div className="border-t border-[var(--color-border)] pt-7">
              <h3 className="font-display text-2xl leading-snug">{f.q}</h3>
              <p className="mt-4 text-[15px] leading-[1.8] text-ink-muted dark:text-slightwhite/70">
                {f.a}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  </>
);

export default Contact;
